import React, { useMemo } from 'react';
import { GameState, DisplayFixture, League, TeamInLeague } from '../../../domain/entities';
import { FixturesHelper } from '../../../services/FixturesHelper';
import { View } from '../../../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper, faTrophy, faFire, faStar, faChartLine, faFaceSmile, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { soundService } from '../../../services/SoundService';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';
import './WeeklyPunditPanel.css';

interface WeeklyPunditPanelProps {
    gameState: GameState;
    pushView: (view: View) => void;
}

interface NewsItem {
    icon: any;
    headline: string;
    description: string;
    links?: { text: string; onClick: () => void }[];
}

export const WeeklyPunditPanel: React.FC<WeeklyPunditPanelProps> = ({ gameState, pushView }) => {
    const { isCompact, toggleView } = usePanelViewPreferences();

    const newsItems = useMemo(() => {
        const items: NewsItem[] = [];
        const currentWeek = gameState.currentWeek;
        const playerTeamLeague = gameState.leagues[gameState.playerLeagueName];
        const playerTeam = playerTeamLeague.teams.find(t => t.name === gameState.playerTeamName);

        if (!playerTeam) return items;

        // Find last week's results
        const lastWeekResults = FixturesHelper.getLeagueFixturesForWeek(gameState.fixtures, gameState.playerLeagueName, currentWeek - 1)
            .filter(f => f.result);

        // Player team result
        const playerTeamResult = lastWeekResults.find(f => f.home === gameState.playerTeamName || f.away === gameState.playerTeamName);
        if (playerTeamResult) {
            const isHome = playerTeamResult.home === gameState.playerTeamName;
            const opponent = isHome ? playerTeamResult.away : playerTeamResult.home;
            const playerGoals = isHome ? playerTeamResult.result!.homeScore : playerTeamResult.result!.awayScore;
            const opponentGoals = isHome ? playerTeamResult.result!.awayScore : playerTeamResult.result!.homeScore;

            if (playerGoals > opponentGoals) {
                items.push({
                    icon: faTrophy,
                    headline: `Victory Over ${opponent}!`,
                    description: `${gameState.playerTeamName} dominated the pitch with a commanding ${playerGoals}-${opponentGoals} win.`,
                    links: [{
                        text: 'View Squad',
                        onClick: () => {
                            soundService.playClick();
                            pushView({ type: 'team', title: 'Squad' });
                        }
                    }]
                });
            } else if (playerGoals === opponentGoals) {
                items.push({
                    icon: faChartLine,
                    headline: `Stalemate with ${opponent}`,
                    description: `A hard-fought draw leaves both teams with a point. ${playerGoals}-${opponentGoals}.`,
                });
            } else {
                items.push({
                    icon: faFire,
                    headline: `Defeat to ${opponent}`,
                    description: `A tough loss ${playerGoals}-${opponentGoals}. Time to regroup and fight back.`,
                    links: [{
                        text: 'View Tactics',
                        onClick: () => {
                            soundService.playClick();
                            pushView({ type: 'tactics', title: 'Tactics' });
                        }
                    }]
                });
            }
        }

        // Top scorer this week
        const topScorer = playerTeam.squad.reduce((max, p) => {
            const weeklyScorerStats = p.seasonStats?.find(s => s.week === currentWeek - 1);
            const goals = weeklyScorerStats?.goals || 0;
            const maxGoals = max.goals || 0;
            return goals > maxGoals ? { ...p, goals } : max;
        }, { id: 0, goals: 0 } as any);

        if (topScorer.goals > 0) {
            items.push({
                icon: faStar,
                headline: `${topScorer.name}'s Stellar Form`,
                description: `${topScorer.name} bagged ${topScorer.goals} goal${topScorer.goals > 1 ? 's' : ''} this week, leading from the front.`,
                links: [{
                    text: `View ${topScorer.name}`,
                    onClick: () => {
                        soundService.playClick();
                        pushView({ type: 'playerDetail', title: `Player: ${topScorer.name}`, playerId: topScorer.id });
                    }
                }]
            });
        }

        // League position change (only show after games have been played)
        const playerTeamRank = playerTeamLeague.teams.findIndex(t => t.name === gameState.playerTeamName);
        if (playerTeamRank !== -1 && currentWeek > 1 && playerTeam.played > 0) {
            if (playerTeamRank === 0) {
                items.push({
                    icon: faTrophy,
                    headline: 'League Leaders!',
                    description: `${gameState.playerTeamName} sits at the top of the table. Eyes on the title!`,
                });
            } else if (playerTeamRank <= 2) {
                items.push({
                    icon: faStar,
                    headline: 'Title Race Heating Up',
                    description: `In 3rd place and within striking distance. The championship is still within reach!`,
                });
            } else if (playerTeamRank >= playerTeamLeague.teams.length - 3) {
                items.push({
                    icon: faFire,
                    headline: 'Relegation Battle',
                    description: `In a precarious position. Every point matters from here.`,
                    links: [{
                        text: 'View League',
                        onClick: () => {
                            soundService.playClick();
                            pushView({ type: 'competitions', title: 'Competitions' });
                        }
                    }]
                });
            }
        }

        // Season opener headline (Week 1)
        if (currentWeek === 1) {
            items.push({
                icon: faNewspaper,
                headline: 'Season Kicks Off!',
                description: `The new season begins! ${gameState.playerTeamName} is ready to make their mark. Time to prove yourself on the pitch.`,
            });
        }

        // Morale insight
        const unhappyCount = playerTeam.squad.filter(p => p.morale === 'Unhappy' || p.morale === 'Furious').length;
        if (unhappyCount > 0) {
            items.push({
                icon: faFaceSmile,
                headline: 'Dressing Room Concerns',
                description: `${unhappyCount} player${unhappyCount > 1 ? 's' : ''} ${unhappyCount > 1 ? 'are' : 'is'} unhappy. Consider team talks.`,
                links: [{
                    text: 'Team Management',
                    onClick: () => {
                        soundService.playClick();
                        pushView({ type: 'team', title: 'Squad', activeTab: 'morale' });
                    }
                }]
            });
        }

        // If no items generated, add a generic motivational message
        if (items.length === 0) {
            items.push({
                icon: faNewspaper,
                headline: 'New Week, New Opportunities',
                description: 'Time to focus on the next challenge. Train hard, play well, win matches!',
            });
        }

        return items;
    }, [gameState, pushView]);

    const isCompactView = isCompact('weeklyPundit');

    return (
        <div className="weekly-pundit-panel">
            <div className="pundit-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FontAwesomeIcon icon={faNewspaper} className="pundit-icon" />
                    <h2 style={{ margin: 0 }}>This Week's Headlines</h2>
                </div>
                <button
                    onClick={() => toggleView('weeklyPundit')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                    title={isCompactView ? 'Show full view' : 'Show compact view'}
                >
                    <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                </button>
            </div>
            <div className="news-items">
                {(isCompactView ? newsItems.slice(0, 1) : newsItems).map((item, idx) => (
                    <div key={idx} className="news-item">
                        <div className="news-item-header">
                            <FontAwesomeIcon icon={item.icon} className="news-icon" />
                            <h3>{item.headline}</h3>
                        </div>
                        <p className="news-description">{item.description}</p>
                        {item.links && item.links.length > 0 && (
                            <div className="news-links">
                                {item.links.map((link, linkIdx) => (
                                    <button
                                        key={linkIdx}
                                        className="link-button"
                                        onClick={link.onClick}
                                    >
                                        {link.text} →
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
